import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../utils/logger';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Heading shown in the fallback UI */
  title?: string;
  /** Optional recovery action (e.g. "Return to World Map"). When provided, a
   *  button with this label resets the boundary and invokes the callback
   *  instead of forcing a full page reload. */
  onReset?: () => void;
  resetLabel?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught a render error:', error, errorInfo.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="error-boundary">
        <div className="error-boundary-card">
          <div className="error-boundary-icon">⚠️</div>
          <h2>{this.props.title || 'Something went wrong'}</h2>
          <p>An unexpected error occurred. Your progress is saved on the server.</p>
          {this.state.error && (
            <pre className="error-boundary-detail">{this.state.error.message}</pre>
          )}
          <div className="error-boundary-actions">
            {this.props.onReset && (
              <button className="error-boundary-btn error-boundary-btn-primary" onClick={this.handleReset}>
                {this.props.resetLabel || 'Try Again'}
              </button>
            )}
            <button
              className={`error-boundary-btn ${this.props.onReset ? '' : 'error-boundary-btn-primary'}`}
              onClick={this.handleReload}
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
